import React, { useState, useMemo } from 'react';
import {
  Menu,
  MenuItem,
  TextField,
  Box,
  Avatar,
  Typography,
  InputAdornment,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  Person as PersonIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { CurrentUserInfoDTO } from '../../api/generated/requests/types.gen';

interface UserSearchMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onSelectUser: (userId: string | undefined) => void;
  users?: CurrentUserInfoDTO[];
  isLoading?: boolean;
  currentUserId?: string;
  getUserDisplayName: (userId: string | undefined) => string;
}

const UserSearchMenu: React.FC<UserSearchMenuProps> = ({
  anchorEl,
  open,
  onClose,
  onSelectUser,
  users = [],
  isLoading = false,
  currentUserId,
  getUserDisplayName
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Filter users based on search term
  const filteredUsers = useMemo(() => {
    if (!searchTerm.trim()) {
      return users;
    }
    
    const searchLower = searchTerm.toLowerCase();
    return users.filter(user => {
      const displayName = getUserDisplayName(user.id);
      const firstName = user.firstName?.toLowerCase() || '';
      const lastName = user.lastName?.toLowerCase() || '';
      const email = user.email?.toLowerCase() || '';
      const login = user.login?.toLowerCase() || '';
      
      return displayName.toLowerCase().includes(searchLower) ||
             firstName.includes(searchLower) ||
             lastName.includes(searchLower) ||
             email.includes(searchLower) ||
             login.includes(searchLower);
    });
  }, [users, searchTerm, getUserDisplayName]);

  const handleClose = () => {
    setSearchTerm('');
    onClose();
  };

  const handleSelectUser = (userId: string | undefined) => {
    onSelectUser(userId);
    handleClose();
  };

  const getUserInitials = (user: CurrentUserInfoDTO) => {
    const firstName = user.firstName || '';
    const lastName = user.lastName || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      PaperProps={{
        style: {
          maxHeight: 400,
          minWidth: 300,
        },
      }}
    >
      {/* Search field */}
      <Box sx={{ p: 1, position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Szukaj użytkownika..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        />
      </Box>

      <Divider />

      {/* No assignment option */}
      <MenuItem 
        onClick={() => handleSelectUser(undefined)}
        sx={{
          backgroundColor: !currentUserId ? 'rgba(0, 0, 0, 0.08)' : 'inherit',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ width: 28, height: 28, bgcolor: 'grey.300', fontSize: '0.75rem' }}>
            <PersonIcon fontSize="small" />
          </Avatar>
          <Typography sx={{ fontSize: '0.8rem' }}>Nieprzypisane</Typography>
          {!currentUserId && <CheckIcon fontSize="small" sx={{ ml: 1 }} />}
        </Box>
      </MenuItem>

      {/* Loading state */}
      {isLoading && (
        <MenuItem disabled>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} />
            <Typography sx={{ fontSize: '0.8rem' }}>Ładowanie użytkowników...</Typography>
          </Box>
        </MenuItem>
      )}

      {/* No results */}
      {!isLoading && searchTerm && filteredUsers.length === 0 && (
        <MenuItem disabled>
          <Typography sx={{ fontSize: '0.8rem' }} color="text.secondary">
            Brak wyników dla "{searchTerm}"
          </Typography>
        </MenuItem>
      )}

      {/* Filtered users */}
      {!isLoading && filteredUsers.map((user) => {
        const isCurrentUser = currentUserId === user.id;
        return (
          <MenuItem 
            key={user.id} 
            onClick={() => handleSelectUser(user.id)}
            sx={{
              backgroundColor: isCurrentUser ? 'rgba(0, 0, 0, 0.08)' : 'inherit',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {user.avatarUrl ? (
                <Avatar 
                  src={user.avatarUrl}
                  sx={{ 
                    width: 28, 
                    height: 28,
                    border: '1px solid',
                    borderColor: 'primary.main',
                  }}
                />
              ) : (
                <Avatar sx={{ width: 28, height: 28, bgcolor: user.color || 'primary.main', fontSize: '0.75rem' }}>
                  {getUserInitials(user) || <PersonIcon fontSize="small" />}
                </Avatar>
              )}
              <Typography sx={{ fontSize: '0.8rem' }}>{getUserDisplayName(user.id)}</Typography>
              {isCurrentUser && <CheckIcon fontSize="small" sx={{ ml: 1 }} />}
            </Box>
          </MenuItem>
        );
      })}
    </Menu>
  );
};

export default UserSearchMenu;